/**
 * Password change for external testers (local sign-in only).
 *
 * Two entry points share this one dialog: the forced change right after a
 * first login / admin reset (`forced` — no Cancel, the app behind it is not
 * usable until the change succeeds) and the voluntary one from the sidebar's
 * key button. Same overlay + card styling as the batch dialogs on Model Updates.
 */
import { useState, type CSSProperties, type FormEvent } from "react";
import { theme } from "../theme";
import {
  ApiError, MIN_PASSWORD_LENGTH, getLocalSession, localAuthApi, updateLocalUser,
} from "../auth/localAuth";

const LABEL: CSSProperties = {
  display: "block", color: theme.textPrimary, fontSize: theme.fontSizeXs,
  fontWeight: 700, marginBottom: 5,
};

const INPUT: CSSProperties = {
  width: "100%", boxSizing: "border-box", padding: "9px 10px", borderRadius: 5,
  border: `1px solid ${theme.borderStrong}`, background: theme.bgInput,
  color: theme.textPrimary, fontFamily: "inherit", fontSize: theme.fontSizeSm, outline: "none",
};

export function ChangePasswordDialog({ forced, onClose, onChanged }: {
  forced: boolean;
  onClose?: () => void;
  onChanged: () => void;
}) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validated as they type so the rule is visible before the button is pressed,
  // but only shown once the field has something in it — an empty form is not
  // yet wrong.
  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== next;
  const sameAsCurrent = next.length > 0 && next === current;
  const canSubmit = !busy && current.length > 0 && next.length >= MIN_PASSWORD_LENGTH
    && confirm === next && !sameAsCurrent;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const session = getLocalSession();
    if (!session) { setError("Your session has ended — please sign in again."); return; }
    setBusy(true);
    setError(null);
    try {
      await localAuthApi.changePassword(session.token, current, next);
      updateLocalUser({ must_change_password: false });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reach the server — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const hint = tooShort
    ? `At least ${MIN_PASSWORD_LENGTH} characters`
    : sameAsCurrent
      ? "The new password must differ from the current one"
      : mismatch
        ? "The two passwords do not match"
        : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="change-password-title"
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(26,28,30,0.62)", backdropFilter: "blur(5px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
      onClick={(event) => { if (!forced && !busy && event.target === event.currentTarget) onClose?.(); }}
    >
      <form
        onSubmit={submit}
        style={{
          width: "min(420px, 100%)", maxHeight: "calc(100vh - 40px)", overflowY: "auto",
          background: theme.bgCard, border: `1px solid ${theme.borderStrong}`, borderRadius: 10,
          padding: 22, boxShadow: "0 12px 40px rgba(0,0,0,0.24)",
        }}
      >
        <h2 id="change-password-title" style={{ margin: 0, fontSize: 19, fontWeight: 700, color: theme.textPrimary }}>
          {forced ? "Set a new password" : "Change password"}
        </h2>
        <p style={{ color: theme.textSecondary, fontSize: theme.fontSizeXs, lineHeight: 1.5, margin: "8px 0 16px" }}>
          {forced
            ? "You signed in with a temporary password. Choose your own before continuing — it must be at least "
            : "Your new password must be at least "}
          {MIN_PASSWORD_LENGTH} characters long.
        </p>

        <div style={{ display: "grid", gap: 12 }}>
          <div>
            <label htmlFor="cp-current" style={LABEL}>Current password</label>
            <input
              id="cp-current" type="password" autoComplete="current-password" autoFocus
              value={current} disabled={busy} onChange={(e) => { setCurrent(e.target.value); setError(null); }}
              style={INPUT}
            />
          </div>
          <div>
            <label htmlFor="cp-new" style={LABEL}>New password</label>
            <input
              id="cp-new" type="password" autoComplete="new-password" minLength={MIN_PASSWORD_LENGTH}
              value={next} disabled={busy} onChange={(e) => { setNext(e.target.value); setError(null); }}
              aria-invalid={tooShort || sameAsCurrent}
              style={{ ...INPUT, borderColor: tooShort || sameAsCurrent ? theme.danger : theme.borderStrong }}
            />
          </div>
          <div>
            <label htmlFor="cp-confirm" style={LABEL}>Confirm new password</label>
            <input
              id="cp-confirm" type="password" autoComplete="new-password"
              value={confirm} disabled={busy} onChange={(e) => { setConfirm(e.target.value); setError(null); }}
              aria-invalid={mismatch}
              style={{ ...INPUT, borderColor: mismatch ? theme.danger : theme.borderStrong }}
            />
          </div>
        </div>

        {hint && (
          <div style={{ marginTop: 8, fontSize: theme.fontSize2Xs, color: theme.danger }}>{hint}</div>
        )}
        {error && (
          <div role="alert" style={{
            marginTop: 12, color: theme.danger, background: theme.dangerSoft,
            border: `1px solid ${theme.danger}`, borderRadius: 5, padding: "8px 10px", fontSize: theme.fontSize2Xs,
          }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
          {!forced && (
            <button
              type="button" disabled={busy} onClick={onClose}
              style={{
                background: "transparent", color: theme.textSecondary, border: `1px solid ${theme.border}`,
                borderRadius: 5, padding: "8px 13px", fontSize: theme.fontSizeXs, fontFamily: "inherit",
                cursor: busy ? "default" : "pointer",
              }}
            >
              Cancel
            </button>
          )}
          <button
            type="submit" disabled={!canSubmit}
            style={{
              background: theme.gold, color: theme.onGold, border: "none", borderRadius: 5,
              padding: "8px 15px", fontSize: theme.fontSizeXs, fontWeight: 700, fontFamily: "inherit",
              cursor: canSubmit ? "pointer" : "default", opacity: canSubmit ? 1 : 0.65,
            }}
          >
            {busy ? "Saving…" : forced ? "Set password" : "Change password"}
          </button>
        </div>
      </form>
    </div>
  );
}
